import React from 'react'

import { ShoppingCartIcon } from 'lucide-react'

import { Alert } from '@repo/ui'
import { EmptyState, Table } from 'components'
import { useReferrerState } from 'hooks'
import { useShopifyOrders } from 'hooks/api/useShopifyOrdersQuery'
// Imported from the modules directly rather than through the OrdersPage barrel, so a
// company detail tab never drags the standalone page component into its chunk.
import { useOrderColumns } from 'pages/internal/OrdersPage/useOrderColumns'
import { useOrderRowActions } from 'pages/internal/OrdersPage/useOrderRowActions'

/**
 * VRG-512 — the company detail page's Orders tab.
 *
 * It had never worked. It took an `orders` prop off `company`, and
 * `GetCompanyResponseDto` has no `orders` member at all, so `company?.orders || []`
 * was `[]` for every company and the tab rendered its empty state unconditionally.
 *
 * Two things changed:
 *
 * 1. It fetches its own data, scoped by `companyId` (mirroring `CompanyMembersPanel`)
 *    rather than receiving a prop. Deliberately NOT added to `GetCompanyResponseDto`:
 *    that use-case is on the hot path for `GET /companies/admin/me`, which every page
 *    load hits, and it carries an explicit note about not putting extra round-trips on
 *    it. A tab nobody has opened should cost nothing.
 *
 * 2. It renders the SHARED columns and row actions (§71), so it cannot drift from
 *    `/internal/orders` again. It previously declared 4 columns of its own against
 *    the page's 15, and they had already diverged in kind: a dead-text order id where
 *    the page has a link, a `status`/"Status" column where the page reads
 *    `payment_status`/"Payment status", and a plain-text fallback for any status
 *    `ORDER_STATUSES` did not carry where the page always renders a chip.
 *
 * SOURCE: the Shopify Admin API, scoped by company. NOT the local `orders` table —
 * the company-scoped read is excluded from the seeded-order fallback
 * (`getLocalOrdersFallback`). Every row in that table is sample data, and no
 * sample/intro data is to surface under `/internal/*`: this console exists to show
 * Vera staff what is really happening across brands. So a company with no real
 * orders gets the empty state below, and a failed fetch gets the error Alert —
 * never a seeded row dressed as an order somebody placed.
 */

interface OrdersPanelProps {
  companyId: string
  companyName?: string
}

/**
 * The Company column is a constant inside one company's page. Module-level so the
 * reference is stable — `useOrderColumns` memoises on it, and an inline literal would
 * rebuild the columns every render.
 */
const OMIT_FIELDS = ['company']

/**
 * The Shopify half of the scoping filters within the fetched window (Shopify cannot
 * filter by a Vera company), so ask for the API's 250-row maximum rather than the
 * default 50 — a company whose orders sit past the window would otherwise read as
 * empty. See `GetShopifyOrdersUseCase.scopeToCompany` for the honest limit.
 */
const SCOPED_FETCH_LIMIT = 250

export const OrdersPanel = ({ companyId, companyName }: OrdersPanelProps) => {
  // §42: the drill-in's "← Back to <label>". The derived label would be "Companies"
  // (the nearest named segment above the record id), which names the list rather than
  // this company — so the company's own name is passed explicitly.
  const referrer = useReferrerState(companyName)

  const { data, isLoading, isError, error, refetch } = useShopifyOrders({
    companyId,
    limit: SCOPED_FETCH_LIMIT,
  })

  const orders = data?.orders ?? []

  const { getActionItems, modals } = useOrderRowActions({ referrer, refetch })
  const columns = useOrderColumns({ referrer, getActionItems, omitFields: OMIT_FIELDS })

  return (
    <>
      {isError && (
        <Alert
          severity="error"
          title="Error loading orders"
          description={error?.message || 'Unknown error occurred'}
        />
      )}

      {/*
        §19 — `loading` drives the shared Table's column-aware skeleton, so the panel
        shows the table's own shape while it fetches rather than a blank tab. The old
        panel passed `loading={false}` because it never fetched anything.

        §22 — `fillBottom` + `topOffset={360}` matches its sibling panels
        (CompanyInvoicesPanel, CompanyMembersPanel). Working-conventions §22 says NOT to
        set `fillBottom` on the Company detail tabs, but all three already do, and §22's
        stated reason is "so content below the grid stays visible" — these tabs have
        nothing below the grid. Left consistent with the siblings rather than silently
        split from them; the discrepancy is reported, not resolved here.
      */}
      <Table
        fillBottom
        loading={isLoading}
        topOffset={360}
        columns={columns}
        rows={orders}
        emptyState={
          <EmptyState
            title="No orders"
            text="This company has no orders yet."
            icon={<ShoppingCartIcon strokeWidth={1} size={32} />}
          />
        }
      />

      {/* The Assign / Buy-Label modals the kebab drives — inert without this render. */}
      {modals}
    </>
  )
}
